import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { CheckCircle2, Clock, FileText, Send, Upload, Star, MessageCircle } from "lucide-react";

const normalizeSettings = (submission) => ({
  enable_rating: Boolean(submission?.assignment_settings?.enable_rating),
  enable_private_discussion: Boolean(submission?.assignment_settings?.enable_private_discussion),
  allow_multiple_files: Boolean(submission?.assignment_settings?.allow_multiple_files),
  enable_round_trips: Boolean(submission?.assignment_settings?.enable_round_trips),
});

const getFiles = (submission, key = "file_urls", legacyKey = "file_url") => {
  const urls = Array.isArray(submission?.[key]) ? submission[key].filter(Boolean) : [];
  const legacy = submission?.[legacyKey];
  return legacy && !urls.includes(legacy) ? [legacy, ...urls] : urls;
};

function RatingInput({ value, onChange }) {
  const rating = Number(value || 0);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="admin-rating-input">
      {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((nextValue) => (
        <button
          key={nextValue}
          type="button"
          onClick={() => onChange(nextValue)}
          className={`rounded-full border px-3 py-1 text-sm font-semibold transition ${rating === nextValue ? "border-amber-400 bg-amber-50 text-amber-700" : "border-gray-200 bg-white hover:bg-amber-50"}`}
        >
          {nextValue}
        </button>
      ))}
      <button type="button" onClick={() => onChange(0)} className="text-xs text-gray-500 hover:text-red-600">
        Effacer
      </button>
    </div>
  );
}

function StarRating({ value }) {
  const rating = Number(value || 0);
  if (!rating) return null;
  return (
    <span className="inline-flex items-center gap-1 text-amber-600">
      {[1, 2, 3, 4, 5].map((index) => {
        const filled = rating >= index;
        const half = !filled && rating >= index - 0.5;
        return (
          <span key={index} className="relative inline-flex h-4 w-4">
            <Star className="h-4 w-4 text-amber-300" />
            {(filled || half) && (
              <span className="absolute inset-0 overflow-hidden" style={{ width: filled ? "100%" : "50%" }}>
                <Star className="h-4 w-4 fill-current text-amber-500" />
              </span>
            )}
          </span>
        );
      })}
      <span className="ml-1 text-xs font-bold">{rating}/5</span>
    </span>
  );
}

export default function AdminAssignments() {
  const [tab, setTab] = useState("pending");
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [rating, setRating] = useState(0);
  const [score, setScore] = useState("");
  const [comment, setComment] = useState("");
  const [correctedFiles, setCorrectedFiles] = useState([]);
  const [messageBody, setMessageBody] = useState("");
  const [messageFiles, setMessageFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const messageFileRef = useRef(null);

  const load = async () => {
    const url = tab === "pending" ? "/assignments/admin/pending" : "/assignments/admin/all";
    const { data } = await api.get(url);
    setList(data);
  };

  useEffect(() => { load(); setActive(null); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectSubmission = (submission) => {
    setActive(submission);
    setRating(Number(submission.rating || (submission.score ? Number(submission.score) / 20 : 0)) || 0);
    setScore(submission.score ?? "");
    setComment(submission.comment || "");
    setCorrectedFiles([]);
    setMessageBody("");
    setMessageFiles([]);
  };

  const uploadFiles = async (files) => {
    const urls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      urls.push(data.url);
    }
    return urls;
  };

  const grade = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const corrected_file_urls = await uploadFiles(correctedFiles);
      const settings = normalizeSettings(active);
      const payload = {
        comment,
        corrected_file_url: corrected_file_urls[0] || active?.corrected_file_url || null,
        corrected_file_urls: corrected_file_urls.length ? corrected_file_urls : getFiles(active, "corrected_file_urls", "corrected_file_url"),
      };
      if (settings.enable_rating) {
        payload.rating = Number(rating || 0);
      } else {
        payload.score = parseFloat(score) || 0;
      }
      const { data } = await api.post(`/assignments/${active.submission_id}/grade`, payload);
      setActive(data);
      setCorrectedFiles([]);
      setMessageBody("");
      setMessageFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } finally {
      setBusy(false);
    }
  };

  const sendPrivateMessage = async () => {
    if (!active || (!messageBody.trim() && messageFiles.length === 0)) return;
    setBusy(true);
    try {
      const file_urls = await uploadFiles(messageFiles);
      const { data } = await api.post(`/assignments/${active.submission_id}/messages`, {
        body: messageBody,
        file_urls,
      });
      setActive((current) => ({ ...current, messages: [...(current?.messages || []), data] }));
      setMessageBody("");
      setMessageFiles([]);
      if (messageFileRef.current) messageFileRef.current.value = "";
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-assignments-page">
      <p className="label">Admin</p>
      <h1 className="font-heading text-3xl font-black mb-6">Devoirs à corriger</h1>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("pending")} className={`px-4 py-2 rounded-full font-semibold ${tab === "pending" ? "bg-brand-green text-white" : "bg-white border"}`} data-testid="admin-assignments-tab-pending">À corriger</button>
        <button onClick={() => setTab("all")} className={`px-4 py-2 rounded-full font-semibold ${tab === "all" ? "bg-brand-green text-white" : "bg-white border"}`} data-testid="admin-assignments-tab-all">Tous</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        <div className="space-y-2">
          {list.length === 0 && <p className="text-sm text-gray-500">Aucun devoir.</p>}
          {list.map((s) => (
            <button key={s.submission_id} onClick={() => selectSubmission(s)} className={`w-full text-left card p-4 hover:bg-emerald-50 ${active?.submission_id === s.submission_id ? "ring-2 ring-brand-green" : ""}`} data-testid={`admin-submission-${s.submission_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-brand-green" />
                <p className="font-semibold text-sm">{s.lesson_title}</p>
              </div>
              <p className="text-xs text-gray-500">{s.user_name} · {new Date(s.submitted_at).toLocaleDateString("fr-FR")}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {s.status === "graded" ? <span className="badge"><CheckCircle2 className="w-3 h-3" /> Corrigé</span> : <span className="badge orange"><Clock className="w-3 h-3" /> En attente</span>}
                {normalizeSettings(s).enable_rating && <StarRating value={s.rating} />}
              </div>
            </button>
          ))}
        </div>

        <div>
          {active ? (() => {
            const settings = normalizeSettings(active);
            const submittedFiles = getFiles(active);
            const correctedFileUrls = getFiles(active, "corrected_file_urls", "corrected_file_url");
            const canDiscuss = settings.enable_private_discussion;
            return (
              <div className="card p-6 space-y-5" data-testid="admin-grade-form">
                <div>
                  <p className="label">Étudiant</p>
                  <p className="font-bold">{active.user_name}</p>
                  <p className="text-xs text-gray-500">Devoir : {active.lesson_title}</p>
                </div>

                {active.text_response && (
                  <div>
                    <p className="label mb-1">Réponse texte</p>
                    <p className="bg-gray-50 p-3 rounded whitespace-pre-line text-sm">{active.text_response}</p>
                  </div>
                )}

                {submittedFiles.length > 0 && (
                  <div>
                    <p className="label mb-2">Fichier(s) rendu(s)</p>
                    <div className="flex flex-wrap gap-2">
                      {submittedFiles.map((url, index) => (
                        <a key={`${url}-${index}`} href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-flex" data-testid="admin-download-submission-btn">📥 Télécharger {submittedFiles.length > 1 ? index + 1 : ""}</a>
                      ))}
                    </div>
                  </div>
                )}

                <hr />

                {settings.enable_rating ? (
                  <div>
                    <p className="label mb-2">Notation 5 étoiles</p>
                    <RatingInput value={rating} onChange={setRating} />
                  </div>
                ) : (
                  <div>
                    <p className="label mb-1">Note (sur 100)</p>
                    <input className="input" type="number" min="0" max="100" value={score} onChange={(e) => setScore(e.target.value)} placeholder="Ex: 85" data-testid="admin-grade-score-input" />
                  </div>
                )}

                <div>
                  <p className="label mb-1">Commentaire / retour</p>
                  <textarea className="input" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tes feedbacks pour l'apprenant..." data-testid="admin-grade-comment-input" />
                </div>

                <div>
                  <p className="label mb-1">Fichier(s) corrigé(s) facultatif(s)</p>
                  {correctedFileUrls.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {correctedFileUrls.map((url, index) => (
                        <a key={`${url}-${index}`} href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-brand-green underline">Correction existante {index + 1}</a>
                      ))}
                    </div>
                  )}
                  <input ref={fileRef} type="file" multiple onChange={(e) => setCorrectedFiles(Array.from(e.target.files || []))} className="hidden" data-testid="admin-corrected-file-input" />
                  <button onClick={() => fileRef.current?.click()} className="btn-ghost"><Upload className="w-4 h-4" /> {correctedFiles.length ? `${correctedFiles.length} fichier(s)` : "Joindre la version corrigée"}</button>
                </div>

                <button onClick={grade} disabled={busy || (!settings.enable_rating && !score)} className="btn-primary w-full justify-center" data-testid="admin-grade-submit-btn">
                  <Send className="w-4 h-4" /> {busy ? "Envoi..." : "Envoyer la correction"}
                </button>

                {canDiscuss && (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 space-y-4">
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2"><MessageCircle className="w-5 h-5 text-brand-green" /> Discussion privée du devoir</h3>
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                      {(active.messages || []).length === 0 && <p className="text-sm text-gray-500">Aucun message.</p>}
                      {(active.messages || []).map((m) => (
                        <div key={m.message_id} className={`rounded-2xl p-3 ${m.sender_role === "admin" ? "bg-white" : "bg-amber-50 border border-amber-100"}`}>
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <p className="font-semibold text-sm">{m.sender_name || (m.sender_role === "admin" ? "Admin" : "Apprenant")}</p>
                            <p className="text-[11px] text-gray-500">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
                          </div>
                          {m.body && <p className="text-sm whitespace-pre-line">{m.body}</p>}
                          {(m.file_urls || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {m.file_urls.map((url, index) => (
                                <a key={`${m.message_id}-${index}`} href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-brand-green underline">Pièce jointe {index + 1}</a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <textarea value={messageBody} onChange={(e) => setMessageBody(e.target.value)} className="input" rows={3} placeholder="Répondre à l'apprenant..." data-testid="admin-assignment-message-input" />
                    <div className="flex flex-wrap gap-2">
                      <input ref={messageFileRef} type="file" multiple onChange={(e) => setMessageFiles(Array.from(e.target.files || []))} className="hidden" data-testid="admin-assignment-message-files-input" />
                      <button type="button" onClick={() => messageFileRef.current?.click()} className="btn-ghost"><Upload className="w-4 h-4" /> {messageFiles.length ? `${messageFiles.length} fichier(s)` : "Joindre"}</button>
                      <button onClick={sendPrivateMessage} disabled={busy || (!messageBody.trim() && messageFiles.length === 0)} className="btn-primary" data-testid="admin-assignment-message-submit"><Send className="w-4 h-4" /> Envoyer</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })() : (
            <div className="card p-10 text-center text-gray-500">Sélectionne un devoir pour le corriger.</div>
          )}
        </div>
      </div>
    </div>
  );
}
